import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import {
  putFileToPresignedUrl,
  resolveFileContentType,
} from "../utils/s3Upload";
import { API_URL } from "../config/api";

const S3_BASE_URL =
  "https://instatenders-supplier-upload.s3.ap-south-1.amazonaws.com";

const STOCK_CATEGORY_OPTIONS = ["Stock", "Non-Stock"];
const MAX_MATERIAL_IMAGES = 5;
const MAX_MATERIAL_ATTACHMENTS = 10;

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }

  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }

  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };

  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }

  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }

  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true || success === "true" || success === 1 || success === "1"
  );
}

function extractCategoriesList(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return [];

  let list =
    dataBlock.categories ||
    dataBlock.categoryList ||
    dataBlock.items ||
    root.categories;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.categories || inner.categoryList || inner.items;
    }
  }

  if (typeof list === "string") list = parseJsonIfString(list);
  return Array.isArray(list) ? list : [];
}

function extractSubcategoriesList(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return [];

  let list =
    dataBlock.subcategories ||
    dataBlock.subcategoryList ||
    dataBlock.items ||
    root.subcategories;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.subcategories || inner.subcategoryList || inner.items;
    }
  }

  if (typeof list === "string") list = parseJsonIfString(list);
  return Array.isArray(list) ? list : [];
}

function extractAttributesList(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return [];

  let list =
    dataBlock.attributes ||
    dataBlock.attributeList ||
    dataBlock.items ||
    root.attributes;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.attributes || inner.attributeList || inner.items;
    }
  }

  if (typeof list === "string") list = parseJsonIfString(list);
  return Array.isArray(list) ? list : [];
}

function buildS3Url(s3Key) {
  const key = String(s3Key || "").trim().replace(/^\/+/, "");
  if (!key) return "";
  if (/^https?:\/\//i.test(key)) return key;
  return `${S3_BASE_URL}/${key}`;
}

function extractS3KeyFromUrl(url) {
  const str = String(url || "");
  if (!str) return "";
  const prefix = `${S3_BASE_URL}/`;
  if (str.startsWith(prefix)) return str.slice(prefix.length);
  const idx = str.indexOf("Material-Images/");
  if (idx >= 0) return str.slice(idx);
  return "";
}

function normalizeExistingMaterialImages(material) {
  if (!material || typeof material !== "object") return [];

  const images = Array.isArray(material.images) ? material.images : null;
  if (images && images.length > 0) {
    return images
      .map((item, index) => {
        if (typeof item === "string") {
          const s3Key = item;
          return {
            s3Key,
            originalFileName: `Image ${index + 1}`,
            contentType: "",
            url: buildS3Url(s3Key),
          };
        }
        if (!item || typeof item !== "object") return null;

        const s3Key =
          item.s3Key || item.key || extractS3KeyFromUrl(item.url || "");
        const url =
          item.url ||
          item.imageUrl ||
          item.publicUrl ||
          (s3Key ? buildS3Url(s3Key) : "");

        if (!s3Key && !url) return null;

        return {
          s3Key: s3Key || extractS3KeyFromUrl(url),
          originalFileName:
            item.originalFileName || item.fileName || `Image ${index + 1}`,
          contentType: item.contentType || "",
          url,
        };
      })
      .filter(Boolean);
  }

  const s3Keys = Array.isArray(material.s3Keys) ? material.s3Keys : null;
  if (s3Keys && s3Keys.length > 0) {
    const originalFileNames = Array.isArray(material.originalFileNames)
      ? material.originalFileNames
      : [];
    const contentTypes = Array.isArray(material.contentTypes)
      ? material.contentTypes
      : [];
    return s3Keys
      .map((s3Key, idx) => ({
        s3Key,
        originalFileName:
          originalFileNames[idx] || `Image ${idx + 1}`,
        contentType: contentTypes[idx] || "",
        url: buildS3Url(s3Key),
      }))
      .filter((x) => x.s3Key);
  }

  return [];
}

function normalizeExistingMaterialAttachments(material) {
  if (!material || typeof material !== "object") return [];
  if (!Array.isArray(material.attachments) || material.attachments.length === 0) {
    return [];
  }

  return material.attachments
    .map((item, index) => {
      if (typeof item === "string") {
        const s3Key = item;
        return {
          s3Key,
          originalFileName: `Attachment ${index + 1}`,
          contentType: "",
          url: buildS3Url(s3Key),
        };
      }
      if (!item || typeof item !== "object") return null;

      const s3Key =
        item.s3Key || item.key || extractS3KeyFromUrl(item.url || "");
      const url =
        item.url ||
        item.downloadUrl ||
        item.publicUrl ||
        (s3Key ? buildS3Url(s3Key) : "");

      if (!s3Key && !url) return null;

      return {
        s3Key: s3Key || extractS3KeyFromUrl(url),
        originalFileName:
          item.originalFileName ||
          item.fileName ||
          item.name ||
          `Attachment ${index + 1}`,
        contentType: item.contentType || "",
        url,
      };
    })
    .filter(Boolean);
}

function attachmentIconClass(att) {
  const type = String(att?.contentType || "").toLowerCase();
  const name = String(att?.originalFileName || "").toLowerCase();
  if (type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) {
    return "fa-solid fa-image";
  }
  if (type.includes("pdf") || name.endsWith(".pdf")) {
    return "fa-solid fa-file-pdf";
  }
  if (
    type.includes("sheet") ||
    type.includes("excel") ||
    /\.(xlsx?|csv)$/i.test(name)
  ) {
    return "fa-solid fa-file-excel";
  }
  if (type.includes("word") || /\.(docx?)$/i.test(name)) {
    return "fa-solid fa-file-word";
  }
  if (type.startsWith("text/") || name.endsWith(".txt")) {
    return "fa-solid fa-file-lines";
  }
  return "fa-solid fa-paperclip";
}

function getPresignedUploadUrl(meta) {
  return String(
    meta?.uploadUrl ||
      meta?.url ||
      meta?.presignedUrl ||
      meta?.signedUrl ||
      "",
  ).trim();
}

function mapPresignMeta(meta, file, fallbackType) {
  return {
    uploadUrl: getPresignedUploadUrl(meta),
    s3Key: meta?.s3Key || meta?.key,
    originalFileName:
      meta?.originalFileName || meta?.fileName || file?.name,
    contentType: meta?.contentType || fallbackType,
  };
}

function normalizePresignFileMetas(responseData, files, contentTypesForPresign) {
  if (!isApiSuccess(responseData)) return null;

  const dataBlock =
    responseData?.data && typeof responseData.data === "object"
      ? responseData.data
      : responseData;
  const nestedBlock =
    dataBlock?.data && typeof dataBlock.data === "object"
      ? dataBlock.data
      : null;

  const fallbackType = (idx) =>
    contentTypesForPresign?.[idx] ||
    resolveFileContentType(files[idx]) ||
    "application/octet-stream";

  const fileLists = [
    dataBlock?.files,
    responseData?.files,
    nestedBlock?.files,
  ];
  for (const list of fileLists) {
    if (Array.isArray(list) && list.length > 0) {
      return list.map((meta, idx) =>
        mapPresignMeta(meta, files[idx], fallbackType(idx)),
      );
    }
  }

  const urlLists = [
    dataBlock?.uploadUrls,
    nestedBlock?.uploadUrls,
    responseData?.uploadUrls,
  ];
  for (const list of urlLists) {
    if (Array.isArray(list) && list.length > 0) {
      const source = dataBlock?.uploadUrls
        ? dataBlock
        : nestedBlock || responseData;
      return list.map((uploadUrl, idx) => ({
        uploadUrl,
        s3Key: source?.s3Keys?.[idx],
        originalFileName: source?.originalFileNames?.[idx] || files[idx]?.name,
        contentType: source?.contentTypes?.[idx] || fallbackType(idx),
      }));
    }
  }

  const single =
    (getPresignedUploadUrl(dataBlock) && dataBlock?.s3Key && dataBlock) ||
    (getPresignedUploadUrl(nestedBlock) && nestedBlock?.s3Key && nestedBlock) ||
    (getPresignedUploadUrl(responseData) &&
      responseData?.s3Key &&
      responseData);

  if (single && files.length === 1) {
    return [mapPresignMeta(single, files[0], fallbackType(0))];
  }

  return null;
}

async function uploadMaterialFiles({ files, materialCode, label = "files" }) {
  if (!files || files.length === 0) return [];
  const trimmedCode = String(materialCode || "").trim();
  if (!trimmedCode) {
    toast.error(`Material code is required before uploading ${label}`, {
      autoClose: 2000,
    });
    return null;
  }

  const contentTypesForPresign = files.map((f) => resolveFileContentType(f));

  const payload = {
    action: "get-material-image-upload-presigned-url",
    materialCode: trimmedCode,
    fileNames: files.map((f) => f.name),
    contentTypes: contentTypesForPresign,
    expiresIn: 7200,
  };

  if (files.length === 1) {
    payload.fileName = files[0].name;
    payload.contentType = contentTypesForPresign[0];
  }

  const response = await axios.post(API_URL, payload);
  const responseData = normalizeApiPayload(parseApiResponse(response.data));
  const fileMetas = normalizePresignFileMetas(
    responseData,
    files,
    contentTypesForPresign,
  );

  if (!fileMetas || fileMetas.length !== files.length) {
    toast.error(
      responseData?.message || `Failed to get material ${label} upload URLs`,
      { autoClose: 2000 },
    );
    return null;
  }

  // S3 PUT via raw fetch — never shared API axios (currentUserId ~44B bug).
  try {
    for (let idx = 0; idx < files.length; idx += 1) {
      const m = fileMetas[idx];
      const file = files[idx];
      const contentType =
        m.contentType ||
        contentTypesForPresign[idx] ||
        "application/octet-stream";
      await putFileToPresignedUrl(m.uploadUrl, file, contentType);
    }
  } catch (error) {
    console.error(`Upload material ${label} error:`, error);
    toast.error(error?.message || `Failed to upload material ${label}`, {
      autoClose: 2500,
    });
    return null;
  }

  return fileMetas.map((m, idx) => ({
    s3Key: m.s3Key,
    originalFileName: m.originalFileName || files[idx].name,
    contentType:
      m.contentType ||
      contentTypesForPresign[idx] ||
      "application/octet-stream",
  }));
}

async function uploadMaterialImages({ files, materialCode }) {
  return uploadMaterialFiles({ files, materialCode, label: "images" });
}

async function uploadMaterialAttachments({ files, materialCode }) {
  return uploadMaterialFiles({ files, materialCode, label: "attachments" });
}

export default function MaterialEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [materialCode, setMaterialCode] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [productName, setProductName] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [uom, setUom] = useState("");
  const [stockCategory, setStockCategory] = useState("");

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [makeBrandOem, setMakeBrandOem] = useState("");
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [filteredAttributeOptions, setFilteredAttributeOptions] = useState([]);
  const [showAttributeDropdown, setShowAttributeDropdown] = useState(false);
  const [selectedAttributeIndex, setSelectedAttributeIndex] = useState(-1);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(true);
  const [makeBrandOemId, setMakeBrandOemId] = useState("");

  const [existingImages, setExistingImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState(new Set());
  const [replaceFilesByS3Key, setReplaceFilesByS3Key] = useState({});
  const [replacePreviewsByS3Key, setReplacePreviewsByS3Key] = useState({});
  const [addedFiles, setAddedFiles] = useState([]);
  const [addedPreviews, setAddedPreviews] = useState([]);

  const [existingAttachments, setExistingAttachments] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState(new Set());
  const [addedAttachmentFiles, setAddedAttachmentFiles] = useState([]);

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateMaterials, setDuplicateMaterials] = useState([]);
  const [duplicateOverlayLeft, setDuplicateOverlayLeft] = useState(0);

  const [materialDescription, setMaterialDescription] = useState("");
  const [isDescriptionManuallyEdited, setIsDescriptionManuallyEdited] = useState(false);

  const getKeptExistingCount = () =>
    existingImages.filter(
      (img) => img?.s3Key && !imagesToRemove.has(img.s3Key),
    ).length;

  const getRemainingImageSlots = () =>
    Math.max(0, MAX_MATERIAL_IMAGES - getKeptExistingCount() - addedFiles.length);

  const getKeptExistingAttachmentCount = () =>
    existingAttachments.filter(
      (att) => att?.s3Key && !attachmentsToRemove.has(att.s3Key),
    ).length;

  const getRemainingAttachmentSlots = () =>
    Math.max(
      0,
      MAX_MATERIAL_ATTACHMENTS -
        getKeptExistingAttachmentCount() -
        addedAttachmentFiles.length,
    );

const selectedCategory = categoryOptions.find(
  (c) => String(c.categoryId || "") === String(categoryId),
) || null;

const selectedSubcategory = subcategoryOptions.find(
  (s) => String(s.subcategoryId || "") === String(subcategoryId),
) || null;

useEffect(() => {
  if (isDescriptionManuallyEdited) return;

  const parts = [
    selectedCategory?.categoryName,
    selectedSubcategory?.subcategoryName,
    productName.trim(),
  ].filter(Boolean);

  let desc = parts.join(" - ");
  if (partNumber.trim()) desc += ` | Part No: ${partNumber.trim()}`;
  if (makeBrandOem.trim()) desc += ` | Make/Brand/OEM: ${makeBrandOem.trim()}`;

  setMaterialDescription(desc);
}, [
  selectedCategory,
  selectedSubcategory,
  productName,
  partNumber,
  makeBrandOem,
  isDescriptionManuallyEdited,
]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-categories",
        page: 1,
        limit: 500,
        sortBy: "categoryName",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const list = Array.isArray(responseData?.data?.categories)
        ? responseData.data.categories
        : extractCategoriesList(responseData);

      setCategoryOptions(isApiSuccess(responseData) && Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Fetch categories error:", e);
      setCategoryOptions([]);
    }
  }, []);

  const fetchUnits = useCallback(async () => {
    setIsLoadingUnits(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-units",
        page: 1,
        limit: 500,
        sortBy: "unit",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : [];
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || "",
          unit: String(item?.unit || "").trim(),
          status: String(item?.status || "").trim(),
        }))
        .filter((item) => item.unit)
        .filter(
          (item) =>
            !item.status || item.status.toLowerCase() === "active",
        );
      setUnitOptions(isApiSuccess(responseData) ? activeUnits : []);
    } catch (e) {
      console.error("Fetch units error:", e);
      setUnitOptions([]);
    } finally {
      setIsLoadingUnits(false);
    }
  }, []);

  const fetchAttributes = useCallback(async () => {
    setIsLoadingAttributes(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-attribute",
        page: 1,
        limit: 500,
        sortBy: "attribute",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      let list = responseData?.data?.attributes;
      if (!Array.isArray(list)) {
        list = extractAttributesList(responseData);
      }
      const activeAttributes = (Array.isArray(list) ? list : [])
        .map((item) => ({
          id: item?._id || item?.id || "",
          attribute: String(item?.attribute || item?.attributeName || "").trim(),
          status: String(item?.status || "").trim(),
        }))
        .filter((item) => item.attribute)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === "active",
        );
      const finalList = isApiSuccess(responseData) ? activeAttributes : [];
      setAttributeOptions(finalList);
      setFilteredAttributeOptions(finalList);
    } catch (e) {
      console.error("Fetch attributes error:", e);
      setAttributeOptions([]);
      setFilteredAttributeOptions([]);
    } finally {
      setIsLoadingAttributes(false);
    }
  }, []);

  const fetchSubcategories = useCallback(async (selectedCategoryId) => {
    const trimmed = String(selectedCategoryId || "").trim();
    if (!trimmed) {
      setSubcategoryOptions([]);
      return;
    }

    try {
      const response = await axios.post(API_URL, {
        action: "get-all-subcategories",
        page: 1,
        limit: 500,
        categoryId: trimmed,
        sortBy: "subcategoryName",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const list = Array.isArray(responseData?.data?.subcategories)
        ? responseData.data.subcategories
        : extractSubcategoriesList(responseData);

      setSubcategoryOptions(isApiSuccess(responseData) && Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Fetch subcategories error:", e);
      setSubcategoryOptions([]);
    }
  }, []);

  const fetchMaterial = useCallback(async () => {
    if (!id) {
      toast.error("Material ID is missing", { autoClose: 2000 });
      navigate("/material");
      return;
    }

    setIsFetching(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-material-by-id",
        id,
      });

      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || "Failed to fetch material details",
          { autoClose: 2000 },
        );
        navigate("/material");
        return;
      }

      const dataBlock = responseData?.data != null ? responseData.data : responseData;
      const material = dataBlock?.material && typeof dataBlock.material === "object"
        ? dataBlock.material
        : dataBlock;

      if (!material || typeof material !== "object") {
        toast.error("Material not found", { autoClose: 2000 });
        navigate("/material");
        return;
      }

      const resolvedCategoryId =
        material?.categoryId ||
        material?.category?.categoryId ||
        material?.category?.id ||
        "";
      const resolvedSubcategoryId =
        material?.subcategoryId ||
        material?.subcategory?.subcategoryId ||
        material?.subcategory?.id ||
        "";
      setMaterialDescription(material?.materialDescription || "");
      setIsDescriptionManuallyEdited(Boolean(material?.materialDescription));
      setMaterialCode(material?.materialCode || material?.code || material?.materialId || "");
      setCategoryId(resolvedCategoryId);
      setSubcategoryId(resolvedSubcategoryId);
      setProductName(material?.productName || material?.materialName || material?.name || "");
      setPartNumber(material?.partNumber || "");
      setMakeBrandOem(
        material?.makeBrandOem || material?.makeBrandOEM || material?.brand || "",
      );
      setMakeBrandOemId(
  material?.makeBrandOemId ||
  material?.attributeRef ||
  material?.attributeId ||
  ""
);
      setHsnCode(material?.hsnCode || material?.hsn || "");
      setUom(material?.uom || material?.unit || "");
      setStockCategory(material?.stockCategory || material?.stockCategoryName || "");

      const normalizedImages = normalizeExistingMaterialImages(material);
      setExistingImages(normalizedImages);
      setImagesToRemove(new Set());
      setReplaceFilesByS3Key({});
      setReplacePreviewsByS3Key({});
      setAddedFiles([]);
      setAddedPreviews([]);

      const normalizedAttachments =
        normalizeExistingMaterialAttachments(material);
      setExistingAttachments(normalizedAttachments);
      setAttachmentsToRemove(new Set());
      setAddedAttachmentFiles([]);
    } catch (e) {
      console.error("Fetch material error:", e);
      toast.error("An error occurred while fetching material details", { autoClose: 2000 });
      navigate("/material");
    } finally {
      setIsFetching(false);
    }
  }, [id, navigate]);

  // Initial load: categories + units + material.
  useEffect(() => {
    fetchCategories();
    fetchUnits();
    fetchAttributes();
    fetchMaterial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCategories, fetchUnits, fetchAttributes]);

  // Reload subcategories when category changes (also after initial material fetch sets categoryId).
  useEffect(() => {
    if (categoryId) fetchSubcategories(categoryId);
  }, [categoryId, fetchSubcategories]);

  useEffect(() => {
    return () => {
      Object.values(replacePreviewsByS3Key).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      addedPreviews.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [replacePreviewsByS3Key, addedPreviews]);


  const handleMakeBrandOemChange = (e) => {
    const value = e.target.value;
    setMakeBrandOem(value);
    setMakeBrandOemId("");
    setShowAttributeDropdown(true);
    setSelectedAttributeIndex(-1);

    if (value.trim() === "") {
      setFilteredAttributeOptions(attributeOptions);
    } else {
      const filtered = attributeOptions.filter((opt) =>
        opt.attribute.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredAttributeOptions(filtered);
    }
  };

  const handleMakeBrandOemKeyDown = (e) => {
    if (!showAttributeDropdown || filteredAttributeOptions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedAttributeIndex((prev) =>
          prev < filteredAttributeOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedAttributeIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (
          selectedAttributeIndex >= 0 &&
          selectedAttributeIndex < filteredAttributeOptions.length
        ) {
          handleAttributeSelect(filteredAttributeOptions[selectedAttributeIndex]);
        } else if (filteredAttributeOptions.length === 1) {
          handleAttributeSelect(filteredAttributeOptions[0]);
        }
        break;
      case "Escape":
        setShowAttributeDropdown(false);
        setSelectedAttributeIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleAttributeSelect = (attributeOption) => {
    setMakeBrandOem(attributeOption.attribute);
    setMakeBrandOemId(attributeOption.id);
    setShowAttributeDropdown(false);
    setSelectedAttributeIndex(-1);
  };

  const toggleRemoveExistingImage = (s3Key) => {
    if (!s3Key) return;
    setImagesToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(s3Key)) next.delete(s3Key);
      else next.add(s3Key);
      return next;
    });
    setReplaceFilesByS3Key((prev) => {
      if (!prev[s3Key]) return prev;
      const next = { ...prev };
      delete next[s3Key];
      return next;
    });
    setReplacePreviewsByS3Key((prev) => {
      if (!prev[s3Key]) return prev;
      URL.revokeObjectURL(prev[s3Key]);
      const next = { ...prev };
      delete next[s3Key];
      return next;
    });
  };

  const handleReplaceExistingImage = (s3Key, file) => {
    if (!s3Key || !file) return;
    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Only image files are allowed", { autoClose: 2000 });
      return;
    }

    setImagesToRemove((prev) => {
      if (!prev.has(s3Key)) return prev;
      const next = new Set(prev);
      next.delete(s3Key);
      return next;
    });

    setReplacePreviewsByS3Key((prev) => {
      if (prev[s3Key]) URL.revokeObjectURL(prev[s3Key]);
      return { ...prev, [s3Key]: URL.createObjectURL(file) };
    });
    setReplaceFilesByS3Key((prev) => ({ ...prev, [s3Key]: file }));
  };

  const clearReplaceExistingImage = (s3Key) => {
    setReplaceFilesByS3Key((prev) => {
      if (!prev[s3Key]) return prev;
      const next = { ...prev };
      delete next[s3Key];
      return next;
    });
    setReplacePreviewsByS3Key((prev) => {
      if (!prev[s3Key]) return prev;
      URL.revokeObjectURL(prev[s3Key]);
      const next = { ...prev };
      delete next[s3Key];
      return next;
    });
  };

  const handleAddImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    if (getRemainingImageSlots() <= 0) {
      toast.error(`Maximum ${MAX_MATERIAL_IMAGES} images allowed`, {
        autoClose: 2000,
      });
      return;
    }

    if (files.length > 1) {
      toast.error("Please select one image at a time", { autoClose: 2000 });
      return;
    }

    const file = files[0];
    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Only image files are allowed", { autoClose: 2000 });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setAddedFiles((prev) => [...prev, file]);
    setAddedPreviews((prev) => [...prev, previewUrl]);
  };

  const removeAddedImage = (index) => {
    setAddedFiles((prev) => prev.filter((_, i) => i !== index));
    setAddedPreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const toggleRemoveExistingAttachment = (s3Key) => {
    if (!s3Key) return;
    setAttachmentsToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(s3Key)) next.delete(s3Key);
      else next.add(s3Key);
      return next;
    });
  };

  const handleAddAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const kept = getKeptExistingAttachmentCount();
    const maxTotal = MAX_MATERIAL_ATTACHMENTS;
    const remaining = Math.max(0, maxTotal - kept - addedAttachmentFiles.length);

    if (remaining <= 0) {
      toast.error(
        `Maximum ${MAX_MATERIAL_ATTACHMENTS} attachments allowed`,
        { autoClose: 2000 },
      );
      return;
    }

    setAddedAttachmentFiles((prev) => {
      const next = [...prev, ...files].slice(0, maxTotal - kept);
      if (prev.length + files.length > remaining) {
        toast.error(
          `Maximum ${MAX_MATERIAL_ATTACHMENTS} attachments allowed`,
          { autoClose: 2000 },
        );
      }
      return next;
    });
  };

  const removeAddedAttachment = (index) => {
    setAddedAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAddedAttachments = () => {
    setAddedAttachmentFiles([]);
  };

  const buildFinalImagesForSubmit = async () => {
    const keptExisting = existingImages.filter(
      (img) => img?.s3Key && !imagesToRemove.has(img.s3Key),
    );

    const replaceEntries = Object.entries(replaceFilesByS3Key).filter(
      ([s3Key, file]) => s3Key && file && !imagesToRemove.has(s3Key),
    );

    const finalCount = keptExisting.length + addedFiles.length;
    if (finalCount > MAX_MATERIAL_IMAGES) {
      throw new Error(`Maximum ${MAX_MATERIAL_IMAGES} images allowed`);
    }

    const filesToUpload = [
      ...replaceEntries.map(([, file]) => file),
      ...addedFiles,
    ];

    let uploadedMetas = [];
    if (filesToUpload.length > 0) {
      const uploaded = await uploadMaterialImages({
        files: filesToUpload,
        materialCode,
      });
      if (uploaded === null) {
        throw new Error("Image upload failed");
      }
      uploadedMetas = uploaded;
    }

    const replacementMeta = new Map();
    replaceEntries.forEach(([oldKey], idx) => {
      replacementMeta.set(oldKey, uploadedMetas[idx]);
    });

    const newUploaded = uploadedMetas.slice(replaceEntries.length);

    const finalImages = keptExisting.map((img) => {
      const replacement = replacementMeta.get(img.s3Key);
      if (replacement) {
        return {
          s3Key: replacement.s3Key,
          originalFileName: replacement.originalFileName,
          contentType: replacement.contentType,
        };
      }
      return {
        s3Key: img.s3Key,
        originalFileName: img.originalFileName,
        contentType: img.contentType,
      };
    });

    return [
      ...finalImages,
      ...newUploaded.map((meta) => ({
        s3Key: meta.s3Key,
        originalFileName: meta.originalFileName,
        contentType: meta.contentType,
      })),
    ];
  };

  const buildFinalAttachmentsForSubmit = async () => {
    const keptExisting = existingAttachments.filter(
      (att) => att?.s3Key && !attachmentsToRemove.has(att.s3Key),
    );

    const finalCount = keptExisting.length + addedAttachmentFiles.length;
    if (finalCount > MAX_MATERIAL_ATTACHMENTS) {
      throw new Error(
        `Maximum ${MAX_MATERIAL_ATTACHMENTS} attachments allowed`,
      );
    }

    let uploadedMetas = [];
    if (addedAttachmentFiles.length > 0) {
      const uploaded = await uploadMaterialAttachments({
        files: addedAttachmentFiles,
        materialCode,
      });
      if (uploaded === null) {
        throw new Error("Attachment upload failed");
      }
      uploadedMetas = uploaded;
    }

    return [
      ...keptExisting.map((att) => ({
        s3Key: att.s3Key,
        originalFileName: att.originalFileName,
        contentType: att.contentType,
      })),
      ...uploadedMetas.map((meta) => ({
        s3Key: meta.s3Key,
        originalFileName: meta.originalFileName,
        contentType: meta.contentType,
      })),
    ];
  };

  const validateForm = () => {
    if (!categoryId) {
      toast.error("Category is required", { autoClose: 2000 });
      return false;
    }
    if (!subcategoryId) {
      toast.error("Subcategory is required", { autoClose: 2000 });
      return false;
    }
    if (!productName.trim()) {
      toast.error("Product name is required", { autoClose: 2000 });
      return false;
    }
    if (!hsnCode.trim()) {
      toast.error("HSN code is required", { autoClose: 2000 });
      return false;
    }
    if (!uom.trim()) {
      toast.error("UOM is required", { autoClose: 2000 });
      return false;
    }
    if (!stockCategory.trim()) {
      toast.error("Stock category is required", { autoClose: 2000 });
      return false;
    }
    if (!materialCode.trim()) {
      toast.error("Material code is missing (required for image upload)", { autoClose: 2000 });
      return false;
    }
    return true;
  };

 
  const checkDuplicateMaterial = async () => {
    try {
      // Match on product name only. Auto description words (category, brand, etc.)
      // dilute % and hide real name matches below the 70% threshold.
      const response = await axios.post(API_URL, {
        action: "check-material-duplicate",
        materialId: id,
        categoryId,
        subcategoryId,
        productName: productName.trim(),
        materialDescription: "",
      });

      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const payload =
        responseData?.data &&
        typeof responseData.data === "object" &&
        !Array.isArray(responseData.data)
          ? { ...responseData, ...responseData.data }
          : responseData;

      const materials = Array.isArray(payload?.materials)
        ? payload.materials
        : [];

      const isDuplicate =
        payload?.duplicate === true ||
        payload?.duplicate === "true" ||
        payload?.duplicate === 1 ||
        payload?.duplicate === "1";

      if (isApiSuccess(payload) && isDuplicate && materials.length > 0) {
        return materials;
      }
      return [];
    } catch (error) {
      console.error("Duplicate Check Error:", error);
      return [];
    }
  };

  const performUpdate = async () => {
    const currentUserId = localStorage.getItem("id");

    try {
      const imagesToSend = await buildFinalImagesForSubmit();
      const attachmentsToSend = await buildFinalAttachmentsForSubmit();

      const hadExistingAttachments = existingAttachments.some(
        (att) => att?.s3Key,
      );
      const clearAllAttachments =
        hadExistingAttachments && attachmentsToSend.length === 0;

      const payload = {
        action: "update-material",
        id,
        updatedBy: currentUserId,
        userId: currentUserId,
        productName: productName.trim(),
        partNumber: partNumber.trim(),
        makeBrandOemId,
        makeBrandOem: makeBrandOem.trim(),
        materialDescription: materialDescription.trim(),
        hsnCode: hsnCode.trim(),
        uom: uom.trim(),
        stockCategory: stockCategory.trim(),
        categoryId: categoryId.trim(),
        subcategoryId: subcategoryId.trim(),
        images: imagesToSend,
        appendImages: false,
        clearImages: false,
      };

      // Attachments: send keep-list (appendAttachments:false) so missing keys
      // are removed from DB + S3. Use clearAttachments when removing all.
      if (clearAllAttachments) {
        payload.clearAttachments = true;
      } else {
        payload.attachments = attachmentsToSend;
        payload.appendAttachments = false;
        payload.clearAttachments = false;
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || "Material updated successfully", {
          autoClose: 2000,
        });

        const cleanup =
          responseData?.attachmentS3Cleanup ||
          responseData?.data?.attachmentS3Cleanup;
        const failedDeletes = Array.isArray(cleanup?.failed)
          ? cleanup.failed.filter(Boolean)
          : [];
        if (failedDeletes.length > 0) {
          toast.warning(
            `${failedDeletes.length} attachment(s) could not be deleted from storage`,
            { autoClose: 3500 },
          );
        }

        setTimeout(() => navigate("/material"), 1500);
      } else {
        toast.error(
          responseData?.message || "Failed to update material",
          { autoClose: 2000 },
        );
      }
    } catch (error) {
      console.error("Update material error:", error);
      if (String(error?.message || "").includes("Maximum")) {
        toast.error(error.message, { autoClose: 2000 });
      } else if (String(error?.message || "").includes("Image upload failed")) {
        toast.error("Failed to upload material images", { autoClose: 2000 });
      } else if (
        String(error?.message || "").includes("Attachment upload failed")
      ) {
        toast.error("Failed to upload material attachments", {
          autoClose: 2000,
        });
      } else {
        const errData = error.response?.data ? parseApiResponse(error.response.data) : null;
        toast.error(
          (typeof errData === "object" && errData?.message) ||
            error?.message ||
            "An error occurred while updating material",
          { autoClose: 2000 },
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    const finalCount = getKeptExistingCount() + addedFiles.length;
    if (finalCount > MAX_MATERIAL_IMAGES) {
      toast.error(`Maximum ${MAX_MATERIAL_IMAGES} images allowed`, { autoClose: 2000 });
      return;
    }

    const finalAttachmentCount =
      getKeptExistingAttachmentCount() + addedAttachmentFiles.length;
    if (finalAttachmentCount > MAX_MATERIAL_ATTACHMENTS) {
      toast.error(
        `Maximum ${MAX_MATERIAL_ATTACHMENTS} attachments allowed`,
        { autoClose: 2000 },
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Duplicate check FIRST — before any image/attachment uploads.
      const duplicates = await checkDuplicateMaterial();
      if (duplicates.length > 0) {
        setDuplicateMaterials(duplicates);
        setShowDuplicateModal(true);
        setIsSubmitting(false);
        return;
      }

      await performUpdate();
    } catch (error) {
      console.error("Duplicate check error:", error);
      toast.error("An error occurred while validating the material", {
        autoClose: 2000,
      });
      setIsSubmitting(false);
    }
  };

  const handleProceedDuplicate = async () => {
    setShowDuplicateModal(false);
    setIsSubmitting(true);
    await performUpdate();
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setDuplicateMaterials([]);
  };

  useEffect(() => {
    if (!showDuplicateModal) return undefined;

    const updateOverlayPosition = () => {
      const contentWrapper = document.querySelector(".content-wrapper");
      const left = contentWrapper
        ? Math.max(0, contentWrapper.getBoundingClientRect().left)
        : 0;
      setDuplicateOverlayLeft(left);
    };

    updateOverlayPosition();
    window.addEventListener("resize", updateOverlayPosition);
    return () => window.removeEventListener("resize", updateOverlayPosition);
  }, [showDuplicateModal]);

  if (isFetching) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Edit Material</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/material">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Material</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/material">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Material Code
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            value={materialCode || "-"}
                            disabled
                            readOnly
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Category
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={categoryId}
                            disabled
                            style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                          >
                            <option value="">Select category</option>
                            {categoryOptions.map((category) => {
                              const idVal = category.categoryId || "";
                              return (
                                <option
                                  key={idVal || category._id || category.id}
                                  value={idVal}
                                >
                                  {idVal} - {category.categoryName || "-"}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Subcategory
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={subcategoryId}
                            disabled
                            style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                          >
                            <option value="">
                              {categoryId ? "Select subcategory" : "Select category first"}
                            </option>
                            {subcategoryOptions.map((subcategory) => {
                              const idVal = subcategory.subcategoryId || "";
                              return (
                                <option
                                  key={idVal || subcategory._id || subcategory.id}
                                  value={idVal}
                                >
                                  {idVal} - {subcategory.subcategoryName || "-"}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

<div className="form-group row">
  <label className="col-12 col-md-4 col-form-label">
    Material Description
  </label>
  <div className="col-12 col-md-8">
    <textarea
      className="form-control"
      rows={2}
      placeholder="Auto-generated from Category, Subcategory, Product Name, Part Number and Make/Brand/OEM — edit if needed"
      value={materialDescription}
      onChange={(e) => {
        setMaterialDescription(e.target.value);
        setIsDescriptionManuallyEdited(true);
      }}
      disabled={isSubmitting}
    />
  </div>
</div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Product Name <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Product Name"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                            disabled={isSubmitting}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Part Number
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Part Number"
                            value={partNumber}
                            onChange={(e) => setPartNumber(e.target.value)}
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Make / Brand / OEM
                        </label>
                        <div className="col-12 col-md-8">
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={
                                isLoadingAttributes
                                  ? "Loading attributes..."
                                  : "Type to search or enter Make / Brand / OEM..."
                              }
                              value={makeBrandOem}
                              onChange={handleMakeBrandOemChange}
                              onKeyDown={handleMakeBrandOemKeyDown}
                              onFocus={() => setShowAttributeDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowAttributeDropdown(false);
                                  setSelectedAttributeIndex(-1);
                                }, 200);
                              }}
                              autoComplete="off"
                              disabled={isSubmitting}
                            />
                            {isLoadingAttributes && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                }}
                              >
                                <i className="fa fa-spinner fa-spin text-muted"></i>
                              </div>
                            )}
                            {showAttributeDropdown &&
                              !isLoadingAttributes &&
                              filteredAttributeOptions.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 1000,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  {filteredAttributeOptions.map(
                                    (option, index) => (
                                      <div
                                        key={option.id || option.attribute}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          handleAttributeSelect(option);
                                        }}
                                        style={{
                                          padding: "8px 12px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #f0f0f0",
                                          backgroundColor:
                                            index === selectedAttributeIndex
                                              ? "#e9ecef"
                                              : "#fff",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor =
                                            "#f8f9fa";
                                          setSelectedAttributeIndex(index);
                                        }}
                                        onMouseLeave={(e) => {
                                          if (index !== selectedAttributeIndex) {
                                            e.currentTarget.style.backgroundColor =
                                              "#fff";
                                          }
                                        }}
                                      >
                                        {option.attribute}
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            {showAttributeDropdown &&
                              !isLoadingAttributes &&
                              makeBrandOem &&
                              filteredAttributeOptions.length === 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    padding: "8px 12px",
                                    zIndex: 1000,
                                    color: "#6c757d",
                                    fontSize: "13px",
                                  }}
                                >
                                  No matching attributes — will be used as
                                  entered
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          HSN Code <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="HSN Code"
                            value={hsnCode}
                            onChange={(e) => setHsnCode(e.target.value)}
                            disabled={isSubmitting}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          UOM <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={uom}
                            onChange={(e) => setUom(e.target.value)}
                            disabled={isSubmitting || isLoadingUnits}
                            required
                          >
                            <option value="">
                              {isLoadingUnits ? "Loading units..." : "Select UOM"}
                            </option>
                            {uom &&
                              !unitOptions.some(
                                (opt) =>
                                  opt.unit.toLowerCase() === uom.toLowerCase(),
                              ) && (
                                <option value={uom}>{uom}</option>
                              )}
                            {unitOptions.map((opt) => (
                              <option key={opt.id || opt.unit} value={opt.unit}>
                                {opt.unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Stock Category <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={stockCategory}
                            onChange={(e) => setStockCategory(e.target.value)}
                            disabled={isSubmitting}
                            required
                          >
                            {STOCK_CATEGORY_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Images
                        </label>
                        <div className="col-12 col-md-8">
                          <small className="text-muted d-block mb-2">
                            Up to {MAX_MATERIAL_IMAGES} images. Use Replace on one
                            image or Remove to delete it. Add new images only if
                            slots are available.
                          </small>

                          {(existingImages?.length || 0) > 0 && (
                            <div className="mt-2">
                              <div className="fwt_600 mb-2" style={{ fontSize: "13px" }}>
                                Existing images
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "10px",
                                }}
                              >
                                {existingImages.map((img, idx) => {
                                  const s3Key = img.s3Key || "";
                                  const isRemoved = imagesToRemove.has(s3Key);
                                  const replacementFile = replaceFilesByS3Key[s3Key];
                                  const replacementPreview =
                                    replacePreviewsByS3Key[s3Key];
                                  const inputId = `replace-material-img-${idx}`;

                                  return (
                                    <div
                                      key={s3Key || `${img.url}-${idx}`}
                                      className="p-2"
                                      style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: "8px",
                                        background: "#fff",
                                      }}
                                    >
                                      <div
                                        className="d-flex align-items-start"
                                        style={{ gap: "12px" }}
                                      >
                                        <div
                                          style={{
                                            width: "72px",
                                            height: "72px",
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                            border: "1px solid #dee2e6",
                                            flexShrink: 0,
                                            opacity: isRemoved ? 0.45 : 1,
                                          }}
                                        >
                                          <img
                                            src={
                                              replacementPreview ||
                                              img.url ||
                                              buildS3Url(s3Key)
                                            }
                                            alt={img.originalFileName || "existing image"}
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              filter: isRemoved
                                                ? "grayscale(0.8)"
                                                : "none",
                                            }}
                                          />
                                        </div>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div
                                            style={{
                                              fontSize: "13px",
                                              wordBreak: "break-word",
                                              textDecoration: isRemoved
                                                ? "line-through"
                                                : "none",
                                            }}
                                          >
                                            {img.originalFileName || s3Key || "Image"}
                                          </div>
                                          {replacementFile && !isRemoved && (
                                            <div
                                              className="text-muted mt-1"
                                              style={{ fontSize: "12px" }}
                                            >
                                              Replace with:{" "}
                                              <span className="fwt_600">
                                                {replacementFile.name}
                                              </span>
                                              <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 ml-2"
                                                onClick={() =>
                                                  clearReplaceExistingImage(s3Key)
                                                }
                                                disabled={isSubmitting}
                                              >
                                                Clear
                                              </button>
                                            </div>
                                          )}
                                        </div>

                                        <div
                                          className="d-flex flex-column"
                                          style={{ gap: "6px", flexShrink: 0 }}
                                        >
                                          <input
                                            id={inputId}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            onChange={(e) =>
                                              handleReplaceExistingImage(
                                                s3Key,
                                                e.target.files?.[0],
                                              )
                                            }
                                            disabled={isSubmitting || isRemoved}
                                          />
                                          <label
                                            htmlFor={inputId}
                                            className={`btn btn-sm mb-0 ${
                                              isRemoved
                                                ? "btn-secondary"
                                                : "btn-outline-primary"
                                            }`}
                                            style={{ cursor: isRemoved ? "not-allowed" : "pointer" }}
                                          >
                                            Replace
                                          </label>
                                          <button
                                            type="button"
                                            className={`btn btn-sm ${
                                              isRemoved
                                                ? "btn-warning"
                                                : "btn-outline-danger"
                                            }`}
                                            onClick={() =>
                                              toggleRemoveExistingImage(s3Key)
                                            }
                                            disabled={isSubmitting}
                                          >
                                            {isRemoved ? "Undo" : "Remove"}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {getRemainingImageSlots() > 0 && (
                            <div className="mt-3">
                              <div className="fwt_600 mb-2" style={{ fontSize: "13px" }}>
                                Add new image
                              </div>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  readOnly
                                  value={
                                    addedFiles.length === 0
                                      ? "No new images selected"
                                      : `${addedFiles.length} new image(s) selected`
                                  }
                                />
                                <div className="input-group-append">
                                  <label
                                    className="btn btn-outline-secondary mb-0"
                                    style={{ cursor: "pointer" }}
                                  >
                                    Browse
                                    <input
                                      type="file"
                                      accept="image/*"
                                      hidden
                                      onChange={handleAddImageChange}
                                      disabled={isSubmitting}
                                    />
                                  </label>
                                </div>
                              </div>
                              <small className="text-muted">
                                {getKeptExistingCount() + addedFiles.length}/
                                {MAX_MATERIAL_IMAGES} images after update.
                              </small>
                            </div>
                          )}

                          {addedPreviews.length > 0 && (
                            <div className="mt-3">
                              <div className="fwt_600 mb-2" style={{ fontSize: "13px" }}>
                                New images to add
                              </div>
                              <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                                {addedPreviews.map((previewUrl, index) => (
                                  <div
                                    key={`${previewUrl}-${index}`}
                                    style={{
                                      position: "relative",
                                      width: "88px",
                                      height: "88px",
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      border: "1px solid #dee2e6",
                                    }}
                                  >
                                    <img
                                      src={previewUrl}
                                      alt={addedFiles[index]?.name || "new image"}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeAddedImage(index)}
                                      disabled={isSubmitting}
                                      style={{
                                        position: "absolute",
                                        top: "4px",
                                        right: "4px",
                                        padding: "0 6px",
                                        lineHeight: "18px",
                                        fontSize: "11px",
                                      }}
                                      title="Remove new image"
                                    >
                                      <i className="fa-solid fa-xmark" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Attachments
                        </label>
                        <div className="col-12 col-md-8">
                          <small className="text-muted d-block mb-2">
                            Up to {MAX_MATERIAL_ATTACHMENTS} files. Remove existing
                            or add new ones (same presigned upload as images).
                          </small>

                          {(existingAttachments?.length || 0) > 0 && (
                            <div className="mt-2">
                              <div
                                className="fwt_600 mb-2"
                                style={{ fontSize: "13px" }}
                              >
                                Existing attachments
                              </div>
                              <ul className="list-group">
                                {existingAttachments.map((att, idx) => {
                                  const s3Key = att.s3Key || "";
                                  const isRemoved =
                                    attachmentsToRemove.has(s3Key);
                                  return (
                                    <li
                                      key={s3Key || `${att.url}-${idx}`}
                                      className="list-group-item d-flex justify-content-between align-items-center py-2"
                                      style={{
                                        opacity: isRemoved ? 0.55 : 1,
                                      }}
                                    >
                                      <div
                                        className="d-flex align-items-center text-truncate mr-2"
                                        style={{
                                          textDecoration: isRemoved
                                            ? "line-through"
                                            : "none",
                                        }}
                                      >
                                        <i
                                          className={`${attachmentIconClass(att)} mr-2`}
                                        />
                                        <span
                                          className="text-truncate"
                                          title={att.originalFileName}
                                        >
                                          {att.originalFileName || s3Key || "File"}
                                        </span>
                                      </div>
                                      <div className="d-flex align-items-center">
                                        {att.url && !isRemoved && (
                                          <a
                                            href={att.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-secondary mr-2"
                                            title="Open"
                                          >
                                            <i className="fa-solid fa-eye" />
                                          </a>
                                        )}
                                        <button
                                          type="button"
                                          className={`btn btn-sm ${
                                            isRemoved
                                              ? "btn-outline-secondary"
                                              : "btn-outline-danger"
                                          }`}
                                          onClick={() =>
                                            toggleRemoveExistingAttachment(s3Key)
                                          }
                                          disabled={isSubmitting}
                                        >
                                          {isRemoved ? "Undo" : "Remove"}
                                        </button>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}

                          {getRemainingAttachmentSlots() > 0 && (
                            <div className="mt-3">
                              <div
                                className="fwt_600 mb-2"
                                style={{ fontSize: "13px" }}
                              >
                                Add new attachments
                              </div>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  readOnly
                                  value={
                                    addedAttachmentFiles.length === 0
                                      ? "No new attachments selected"
                                      : `${addedAttachmentFiles.length} new attachment(s) selected`
                                  }
                                />
                                <div className="input-group-append">
                                  <label
                                    className="btn btn-outline-secondary mb-0"
                                    style={{ cursor: "pointer" }}
                                  >
                                    Browse
                                    <input
                                      type="file"
                                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                                      multiple
                                      hidden
                                      onChange={handleAddAttachmentChange}
                                      disabled={isSubmitting}
                                    />
                                  </label>
                                  {addedAttachmentFiles.length > 0 && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger"
                                      onClick={clearAddedAttachments}
                                      disabled={isSubmitting}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                              </div>
                              <small className="text-muted">
                                {getKeptExistingAttachmentCount() +
                                  addedAttachmentFiles.length}
                                /{MAX_MATERIAL_ATTACHMENTS} attachments after
                                update.
                              </small>
                            </div>
                          )}

                          {addedAttachmentFiles.length > 0 && (
                            <ul className="list-group mt-3">
                              {addedAttachmentFiles.map((file, index) => (
                                <li
                                  key={`${file.name}-${file.size}-${index}`}
                                  className="list-group-item d-flex justify-content-between align-items-center py-2"
                                >
                                  <span
                                    className="text-truncate mr-2"
                                    title={file.name}
                                  >
                                    <i className="fa-solid fa-paperclip mr-2" />
                                    {file.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeAddedAttachment(index)}
                                    disabled={isSubmitting}
                                    title="Remove"
                                  >
                                    <i className="fa-solid fa-xmark" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isSubmitting || !materialCode || !categoryId || !subcategoryId}
                      >
                        {isSubmitting ? "Updating..." : "Update Material"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDuplicateModal && (
        <div
          tabIndex="-1"
          style={{
            position: "fixed",
            top: 0,
            left: duplicateOverlayLeft,
            right: 0,
            bottom: 0,
            width: `calc(100vw - ${duplicateOverlayLeft}px)`,
            height: "100vh",
            background: "rgba(0,0,0,.45)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            boxSizing: "border-box",
          }}
        >
          <div
            className="modal-content"
            style={{
              maxWidth: "1100px",
              width: "100%",
              margin: 0,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="modal-header bg-info text-white">
              <h5 className="modal-title">
                <i className="fa fa-exclamation-triangle mr-2"></i>
                Material Match Found
              </h5>

              <button
                type="button"
                className="close text-white"
                onClick={handleCancelDuplicate}
              >
                <span className="text-white">&times;</span>
              </button>
            </div>

            <div className="modal-body" style={{ overflowY: "auto" }}>
              <p className="text-danger mb-3">
                Similar material(s) found with at least 70% word match. Please
                review the existing records before updating.
              </p>

              <div
                className="table-responsive"
                style={{ maxHeight: "350px", overflowY: "auto" }}
              >
                <table
                  className="table table-bordered table-hover mb-0"
                  style={{ tableLayout: "fixed", width: "100%" }}
                >
                  <thead className="thead-light sticky-top">
                    <tr>
                      <th style={{ width: "5%" }}>S.No</th>
                      <th style={{ width: "16%" }}>Match %</th>
                      <th style={{ width: "14%" }}>Material Code</th>
                      <th style={{ width: "22%" }}>Product Name</th>
                      <th style={{ width: "20%" }}>Description</th>
                      <th style={{ width: "23%" }}>Matched Words</th>
                    </tr>
                  </thead>

                  <tbody>
                    {duplicateMaterials.map((item, index) => {
                      const matchPercent = Number(item.matchPercent) || 0;
                      const badgeClass =
                        matchPercent >= 90
                          ? "badge-danger"
                          : matchPercent >= 80
                            ? "badge-warning"
                            : "badge-info";
                      const matchedWords = Array.isArray(item.matchedWords)
                        ? item.matchedWords.join(", ")
                        : "-";

                      return (
                        <tr
                          key={
                            item.materialCode || item._id || `dup-${index}`
                          }
                        >
                          <td>{index + 1}</td>
                          <td style={{ wordBreak: "break-word" }}>
                            <span className={`badge ${badgeClass}`}>
                              {matchPercent}%
                            </span>
                            {item.matchSummary ? (
                              <div className="small text-muted mt-1">
                                {item.matchSummary}
                              </div>
                            ) : null}
                          </td>
                          <td style={{ wordBreak: "break-word" }}>
                            <strong>{item.materialCode || "-"}</strong>
                          </td>
                          <td style={{ wordBreak: "break-word" }}>
                            {item.productName || "-"}
                          </td>
                          <td style={{ wordBreak: "break-word" }}>
                            {item.materialDescription || "-"}
                          </td>
                          <td
                            style={{
                              wordBreak: "break-word",
                              fontSize: "14px",
                            }}
                          >
                            {matchedWords}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelDuplicate}
              >
                Cancel
              </button>

              <button
                className="btn btn-info"
                onClick={handleProceedDuplicate}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}